import { useEffect, useRef, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/auth-context';
import { getApiBaseUrlEnv, isAuthenticated, refreshSession } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type { NotificationItem } from '@/lib/types';

/**
 * Attempt to refresh the access cookie via the refresh endpoint.
 * Returns true on success (new access cookie set by the server).
 */
async function refreshAccessCookie(): Promise<boolean> {
  try {
    await refreshSession();
    return true;
  } catch {
    return false;
  }
}

/** Payload shapes pushed by the Django Channels consumer. */
interface WsNotificationMessage {
  type: 'new_notification';
  notification: NotificationItem;
}

interface WsBoardUpdate {
  type: 'board_update';
  organization_id: number;
}

interface WsAuthResponse {
  type: 'auth_ok' | 'auth_error';
  detail?: string;
}

interface WsMembershipInactive {
  type: 'membership_inactive';
  organization_id?: number | null;
  organization_name?: string | null;
}

/**
 * Back-off between reconnect attempts. Capped at three attempts so an
 * unavailable WebSocket backend (e.g. a REST-only local run) produces at most
 * three browser "connection failed" lines instead of an endless stream, after
 * which REST polling takes over silently.
 */
export const RECONNECT_DELAYS = [1000, 4000, 15000];

/** Max consecutive failures before we give up on WebSocket and rely on REST polling. */
export const MAX_RETRY_COUNT = RECONNECT_DELAYS.length;

export const WS_FALLBACK_MESSAGE =
  `[notifications] WebSocket unavailable after ${MAX_RETRY_COUNT} attempts; falling back to REST polling.`;

function getWsBaseUrl(): string {
  // An API base URL override repoints the whole backend, so it wins over
  // the more narrowly-scoped VITE_WS_BASE_URL.
  const explicitWs =
    import.meta.env.VITE_API_BASE_URL_OVERRIDE || import.meta.env.VITE_WS_BASE_URL;
  if (explicitWs) {
    return explicitWs.replace(/^http/, 'ws').replace(/\/+$/, '');
  }

  // Derive the WebSocket base URL from the API base URL when available so
  // local/E2E setups with a custom VITE_API_BASE_URL connect to the same host.
  const apiBase = getApiBaseUrlEnv() || (
    import.meta.env.PROD ? 'https://api.h3ight.com' : 'http://localhost:8000'
  );
  if (apiBase) {
    return apiBase.replace(/^http/, 'ws').replace(/\/+$/, '');
  }

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  if (import.meta.env.PROD) {
    return `${protocol}//api.h3ight.com`;
  }
  return `${protocol}//localhost:8000`;
}

/** Polling interval when WebSocket is connected (background safety net). */
export const WS_CONNECTED_POLL_INTERVAL = 60_000;

/** Polling interval when WebSocket is disconnected (active fallback). */
export const WS_DISCONNECTED_POLL_INTERVAL = 15_000;

/**
 * Hook that maintains a WebSocket connection to the notifications endpoint.
 *
 * - Automatically connects when the user is authenticated.
 * - Shows a toast for every incoming notification.
 * - Invalidates the React Query notification caches so lists / badge counts
 *   update immediately.
 * - Reconnects with exponential back-off on unexpected disconnects.
 * - After exhausting all retries, falls back to REST polling by signalling
 *   via ``wsConnected`` — the notification queries in ``use-platform.ts``
 *   shorten their ``refetchInterval`` accordingly.
 *
 * Returns ``{ wsConnected }`` so that other hooks can adjust their behaviour.
 */
export function useRealtimeNotifications(): { wsConnected: boolean } {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const organizationId = user?.organization ?? null;
  const [connectionState, setConnectionState] = useState<{
    organizationId: typeof organizationId;
    connected: boolean;
  }>({ organizationId, connected: false });
  const wsConnected =
    connectionState.organizationId === organizationId &&
    connectionState.connected;
  const wsRef = useRef<WebSocket | null>(null);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountedRef = useRef(false);
  const refreshAttemptedRef = useRef(false);
  const fallbackLoggedRef = useRef(false);
  const membershipInactiveRef = useRef<{
    organizationId: number | null;
    organizationName: string;
  } | null>(null);
  const membershipInactiveDispatchedRef = useRef(false);

  const invalidateNotifications = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
  }, [queryClient]);

  const invalidateBoard = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.staffingBoard.all });
  }, [queryClient]);

  const dispatchMembershipInactive = useCallback((detail?: {
    organizationId: number | null;
    organizationName: string;
  }) => {
    if (detail) membershipInactiveRef.current = detail;
    if (membershipInactiveDispatchedRef.current) return;
    membershipInactiveDispatchedRef.current = true;
    window.dispatchEvent(new CustomEvent('membership-inactive', {
      detail: membershipInactiveRef.current ?? {
        organizationId,
        organizationName: '',
      },
    }));
  }, [organizationId]);

  const connect = useCallback(() => {
    if (unmountedRef.current) return;
    if (!isAuthenticated()) return;

    // Close stale connection if any
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Connect WITHOUT token in the URL — token is sent as first message
    const url = `${getWsBaseUrl()}/ws/notifications/`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      // No client-side action needed — the httpOnly access cookie is sent
      // automatically with the WebSocket handshake and the server validates
      // it during connect().  We just wait for the auth_ok message.
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as
          | WsNotificationMessage
          | WsBoardUpdate
          | WsAuthResponse
          | WsMembershipInactive;

        if (data.type === 'auth_ok') {
          retryCountRef.current = 0;
          refreshAttemptedRef.current = false;
          fallbackLoggedRef.current = false;
          setConnectionState({ organizationId, connected: true });
          return;
        }
        if (data.type === 'auth_error') {
          return;
        }
        if (data.type === 'membership_inactive') {
          dispatchMembershipInactive({
            organizationId: data.organization_id ?? organizationId,
            organizationName: data.organization_name ?? '',
          });
          return;
        }
        if (data.type === 'new_notification' && 'notification' in data && data.notification) {
          const n = data.notification;
          toast(n.title, { description: n.message });
          invalidateNotifications();
        }
        if (data.type === 'board_update') {
          invalidateBoard();
        }
      } catch {
        // ignore malformed messages
      }
    };

    ws.onclose = (event) => {
      // Ignore if this is a stale connection (a new one was already created)
      if (wsRef.current !== ws) return;
      wsRef.current = null;
      setConnectionState((current) =>
        current.organizationId === organizationId
          ? { ...current, connected: false }
          : current,
      );

      if (event.code === 4003) {
        dispatchMembershipInactive();
        return;
      }

      if (event.code === 4002) {
        if (refreshAttemptedRef.current) return;
        refreshAttemptedRef.current = true;
        refreshAccessCookie().then((ok) => {
          if (ok && !unmountedRef.current) {
            // eslint-disable-next-line react-hooks/immutability
            connect();
          }
        });
        return;
      }

      // 4001 = auth rejected; don't retry
      if (event.code === 4001 || unmountedRef.current) return;

      // Stop retrying after max attempts — REST polling takes over.
      // Log once so the console explains the fallback instead of filling
      // up with reconnect failures.
      if (retryCountRef.current >= MAX_RETRY_COUNT) {
        if (!fallbackLoggedRef.current) {
          fallbackLoggedRef.current = true;
          console.info(WS_FALLBACK_MESSAGE);
        }
        return;
      }

      const delay =
        RECONNECT_DELAYS[
          Math.min(retryCountRef.current, RECONNECT_DELAYS.length - 1)
        ];
      retryCountRef.current += 1;
      retryTimerRef.current = setTimeout(connect, delay);
    };

    ws.onerror = () => {
      // The close handler will fire after this and handle reconnection.
      // No user-visible error — WebSocket failures are silent; REST polling
      // provides the fallback.
    };
  }, [
    dispatchMembershipInactive,
    invalidateNotifications,
    invalidateBoard,
    organizationId,
  ]);

  useEffect(() => {
    unmountedRef.current = false;
    retryCountRef.current = 0;
    refreshAttemptedRef.current = false;
    fallbackLoggedRef.current = false;
    membershipInactiveRef.current = null;
    membershipInactiveDispatchedRef.current = false;
    connect();

    return () => {
      unmountedRef.current = true;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect, organizationId]);

  return { wsConnected };
}
